import React, { useEffect, useMemo } from "react";
import {
  Table,
  Card,
  Empty,
  Spin,
  Statistic,
  Tooltip,
  Row,
  Col,
} from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMyPaidOvertime,
  clearMyPaidOvertimeState,
} from "../redux/slieces/paidOvertimeSlice";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const MyPaidOvertime = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { myPaidOvertimeEntries, myLoading, myError } = useSelector(
    (state) => state.paidOvertime
  );

  useEffect(() => {
    dispatch(clearMyPaidOvertimeState());
    dispatch(fetchMyPaidOvertime());
    return () => dispatch(clearMyPaidOvertimeState());
  }, [dispatch]);

  const entries = myPaidOvertimeEntries || [];

  // Summary statistics
  const totalMinutes = useMemo(
    () =>
      entries.reduce(
        (sum, entry) => sum + (entry.hours || 0) * 60 + (entry.minutes || 0),
        0
      ),
    [entries]
  );
  const totalH = Math.floor(totalMinutes / 60);
  const totalM = totalMinutes % 60;

  const columns = [
    {
      title: t("paidOvertime.date"),
      dataIndex: "date",
      key: "date",
      render: (text) => dayjs(text).format("DD.MM.YYYY"),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      defaultSortOrder: "descend",
    },
    {
      title: t("paidOvertime.duration"),
      key: "duration",
      sorter: (a, b) =>
        ((a.hours || 0) * 60 + (a.minutes || 0)) - ((b.hours || 0) * 60 + (b.minutes || 0)),
      render: (_, record) => `${record.hours}h ${record.minutes}m`,
    },
    {
      title: t("paidOvertime.comment"),
      dataIndex: "comment",
      key: "comment",
      ellipsis: { showTitle: false },
      render: (text) => (
        <Tooltip title={text || undefined}>{text || "—"}</Tooltip>
      ),
    },
  ];

  return (
    <div data-testid="my-paid-overtime" style={{ padding: "8px 0" }}>
      {myLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : myError ? (
        <Empty description={myError} />
      ) : entries.length === 0 ? (
        <Empty description={t("paidOvertime.noEntriesEmployee")} />
      ) : (
        <>
          {/* Summary statistics card — only shown when data is loaded */}
          <Card size="small" style={{ marginBottom: 16 }} data-testid="my-overtime-summary">
            <Row gutter={32}>
              <Col>
                <Statistic
                  title={t("paidOvertime.totalOvertime")}
                  value={`${totalH}h ${totalM}m`}
                  prefix={<ClockCircleOutlined />}
                />
              </Col>
              <Col>
                <Statistic
                  title={t("paidOvertime.totalEntries")}
                  value={entries.length}
                />
              </Col>
            </Row>
          </Card>

          <Table
            dataSource={entries}
            columns={columns}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} ${t("of")} ${total} ${t("paidOvertime.entries")}`,
            }}
            size="small"
            scroll={{ x: true }}
          />
        </>
      )}
    </div>
  );
};

export default MyPaidOvertime;
